import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetProgramsQuery } from '../../../redux/api/manufacturing';
import EmbedHeader from './EmbedHeader';
import EmbedProgrammingPage from './EmbedProgrammingPage';
import {
	embedProgramLookupParams,
	formatEmbedOrderPartLabel,
	readEmbedOrderPartContext,
} from './embedLookup';

import './embed.css';

const Shell: React.FC<{
	title: string;
	tone: 'loading' | 'error';
	children: React.ReactNode;
}> = ({ title, tone, children }) => (
	<div className="embed-program-review">
		<EmbedHeader title={title} mode="programming" />
		<div className={`embed-program-review__${tone}`}>{children}</div>
	</div>
);

/**
 * Entry point for the Fusion programming palette. When a program already
 * exists it is loaded by id; otherwise order/part names are passed through so
 * ``Start Programming`` can create the row on first use.
 */
const EmbedProgramProgrammingByFilename: React.FC = () => {
	const [searchParams] = useSearchParams();
	const context = readEmbedOrderPartContext(searchParams);
	const label = context ? formatEmbedOrderPartLabel(context) : 'Program';

	const { data, isLoading, isError } = useGetProgramsQuery(
		embedProgramLookupParams(context!),
		{ skip: !context }
	);

	if (!context) {
		return (
			<Shell title="Program" tone="error">
				No order and part were provided.
			</Shell>
		);
	}

	if (isLoading) {
		return (
			<Shell title={label} tone="loading">
				Loading program…
			</Shell>
		);
	}

	if (isError) {
		return (
			<Shell title={label} tone="error">
				Unable to look up {label}.
			</Shell>
		);
	}

	const program = data?.results?.[0];

	return (
		<EmbedProgrammingPage
			programId={program?.id}
			orderName={context.orderName}
			partName={context.partName}
		/>
	);
};

export default EmbedProgramProgrammingByFilename;
